import { arrayOf, bool, func, number, object } from "prop-types";
import { useTranslation } from "react-i18next";
import { difference } from "lodash";
import { setSelectionColor } from "@utils";
import { useXlsImporterSettings } from "@tools";
import { AddButtonFull, InfoLabel } from "@components";
import { Grid, IconButton, Tooltip } from "@material-ui/core";
import { ColumnSelector, WorkTypeSelector } from "../components";
import { Trash } from "@assets";
import { ListAlt } from "@material-ui/icons";
import useStyle from "@tools/XlsImporter/components/StepsFactory/steps/Steps.style";
import classnames from "classnames";

export const WorkTypeColumnSelector = ({
  spreadsheet,
  availableColumns,
  takenColumns,
  colorColumn,
  handleColumnSelection,
  columnMode,
  moduleMode,
  setColumnMode,
  setModuleMode,
  hideColumnSelection,
}) => {
  const { t } = useTranslation();
  const settings = useXlsImporterSettings();
  const classes = useStyle();
  
  const {
    currentBreakdownData,
    importWorkTypes,
    useMinMax,
    titleColumnsLength,
  } = settings;
  
  const { selection } = currentBreakdownData;
  
  const handleColumnSelectMode = (index) => () => {
    if (moduleMode !== null) setModuleMode(null);
    setColumnMode(index === columnMode ? null : index);
  };
  
  const handleColumnSelectionSelect = (colIndex) => (e) => {
    handleColumnSelection(e.target.value, colIndex);
  }
  
  const handleColumnMaxSelectionSelect = (colIndex) => (e) => {
    handleColumnSelection(e.target.value, colIndex, true);
  }
  
  const removeColumn = (index) => () => {
    if(typeof importWorkTypes[index]?.column === "number")
      setSelectionColor(
        [...Array(selection[3] - selection[1] + 1).keys()],
        [
          importWorkTypes[index].column,
          ...(typeof importWorkTypes[index].columnMax === "number"
            ? [importWorkTypes[index].columnMax]
            : []),
        ],
        null,
        spreadsheet.current
      );
    
    settings.setImportWorkTypes([
      ...importWorkTypes.slice(0, index),
      ...importWorkTypes.slice(index + 1),
    ]);
  };
  
  const availableWorkTypeColumns = availableColumns.filter(c => c > titleColumnsLength-1);
  
  const addWorkType = () => {
    let column = -1;
    
    if(importWorkTypes.length) {
      const nextColumn = importWorkTypes?.slice(-1)?.[0]?.column + 1;
      column = availableWorkTypeColumns.find(c => c >= nextColumn && !takenColumns.includes(c));
    }
    
    if(column <= 0)
      column = availableWorkTypeColumns.find(c => !takenColumns.includes(c));
    if (column <= 0)
      column = titleColumnsLength + importWorkTypes.length;
    
    settings.setImportWorkTypes([
      ...importWorkTypes,
      { name: "", column },
    ]);
    colorColumn([column]);
  };
  
  return <Grid item container direction="column">
    <InfoLabel spacing={0} label="views.import.sub_titles.work_types" className="text-secondary text-xs mb-1" />
    <Grid
      item container
      alignItems="center"
      spacing={1}
    >
      {
        importWorkTypes.map((s, i) => {
          const columnSelectorProps = {
            name: s.name,
            availableColumns: availableColumns.filter(c => c > titleColumnsLength-1),
            takenColumns,
            useMinMax,
          };

          return (
            <Grid item xs={6} sm={4} md={3} key={s.name + i}>
              <Grid
                item container
                justifyContent="space-between"
                alignItems="center"
                wrap="nowrap"
                className={classnames(
                  "py-1 px-2 rounded gap-1",
                  classes.bordered,
                )}
              >
                <WorkTypeSelector index={i} name={s.name} id={s.id} isCustom={s.xls} />
                <ColumnSelector
                  allowClearValue
                  {...columnSelectorProps}
                  selectedColumn={s.column}
                  onChange={handleColumnSelectionSelect(i)}
                />
                {
                  useMinMax &&
                  <ColumnSelector
                    allowClearValue
                    {...columnSelectorProps}
                    selectedColumn={s.columnMax}
                    onChange={handleColumnMaxSelectionSelect(i)}
                  />
                }
                <Grid
                  item container
                  wrap="nowrap"
                  className="w-max"
                >
                  {
                    !hideColumnSelection &&
                    <IconButton
                      onClick={handleColumnSelectMode(i)}
                      color={columnMode === i ? "primary" : "secondary"}
                    >
                      <Tooltip title={t("views.import.select_column")}>
                        <ListAlt />
                      </Tooltip>
                    </IconButton>
                  }
                  <IconButton
                    size="small"
                    onClick={removeColumn(i)}
                    color={columnMode === i ? "primary" : "secondary"}
                    className="ml-1"
                  >
                    <Tooltip title={t("views.import.remove_column")}>
                      <Trash color="error" />
                    </Tooltip>
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          )
        })
      }
      <Grid item xs={6} sm={4} md={3}>
        <AddButtonFull
          onClick={addWorkType}
          className="w-max"
          disabled={difference(availableWorkTypeColumns, takenColumns).length === 0}
        >
          {t("views.import.add_column")}
        </AddButtonFull>
      </Grid>
    </Grid>
  </Grid>
}

WorkTypeColumnSelector.propTypes = {
  spreadsheet: object.isRequired,
  availableColumns: arrayOf(number).isRequired,
  takenColumns: arrayOf(number).isRequired,
  colorColumn: func.isRequired,
  handleColumnSelection: func.isRequired,
  columnMode: number.isRequired,
  moduleMode: number.isRequired,
  setColumnMode: func.isRequired,
  setModuleMode: func.isRequired,
  hideColumnSelection: bool,
}
